import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CustomerDailyAreaChart, CustomerSegmentsPieChart } from "../../admin-shared/analytics/AnalyticsChartKit";
import Loader from "../components/Loader";
import { fetchPartnerCustomerAnalytics, segmentBadgeClass, segmentLabel } from "../utils/partnerCustomersApi";
import "./PartnerCheckinAnalytics.css";
import "./Customers.css";
import HqFranchiseFilterField from "../../admin-shared/analytics/HqFranchiseFilterField";

const todayIso = () => {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
};

const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
};

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");
const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(
    Number(value || 0),
  );

const MetricCard = ({ label, value, hint }) => (
  <article className="pca-metric">
    <p className="pca-metric__label">{label}</p>
    <p className="pca-metric__value">{value}</p>
    {hint ? <p className="pca-metric__hint">{hint}</p> : null}
  </article>
);

export default function PartnerCustomerAnalytics({
  portalBase = "/nyafaz-ep/partner",
  portalLabel = "Your franchise",
  subtitle = "Guest segments, repeat visits, sales, and check-in trends — scoped to your outlet.",
  fetchCustomerAnalytics = fetchPartnerCustomerAnalytics,
  hqApi = null,
}) {
  const navigate = useNavigate();
  const [franchiseId, setFranchiseId] = useState("");
  const [fromDate, setFromDate] = useState(() => daysAgoIso(29));
  const [toDate, setToDate] = useState(() => todayIso());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [analytics, setAnalytics] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchCustomerAnalytics({
        fromDate,
        toDate,
        franchiseId: franchiseId || undefined,
      });
      setAnalytics(data);
    } catch (err) {
      setError(err?.message || "Failed to load customer analytics");
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, franchiseId, fetchCustomerAnalytics]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = analytics?.summary || {};
  const totals = analytics?.totals || {};
  const engagement = analytics?.engagement || {};
  const segments = useMemo(
    () => (Array.isArray(analytics?.segments) ? analytics.segments : []),
    [analytics],
  );
  const daily = useMemo(
    () => (Array.isArray(analytics?.dailyActivity) ? analytics.dailyActivity : []),
    [analytics],
  );
  const topCustomers = useMemo(
    () => (Array.isArray(analytics?.topCustomers) ? analytics.topCustomers : []),
    [analytics],
  );

  if (loading && !analytics) return <Loader />;

  return (
    <div className="pca-page">
      <div className="pca-page__header">
        <div>
          <p className="pca-page__eyebrow">{portalLabel}</p>
          <h1 className="pca-page__title">Customer analytics</h1>
          <p className="pca-page__subtitle">{subtitle}</p>
        </div>
        <div className="pca-page__actions">
          {portalBase.includes("/partner") ? (
            <Link to={`${portalBase}/customers`} className="pca-page__link">
              Customer list →
            </Link>
          ) : (
            <Link to={`${portalBase}/overview`} className="pca-page__link">
              ← Overview
            </Link>
          )}
          <button type="button" className="pca-page__refresh" onClick={load} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="pca-filters">
        {hqApi ? (
          <HqFranchiseFilterField api={hqApi} value={franchiseId} onChange={setFranchiseId} />
        ) : null}
        <label className="pca-filters__field">
          <span>From</span>
          <input type="date" value={fromDate} max={toDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label className="pca-filters__field">
          <span>To</span>
          <input
            type="date"
            value={toDate}
            min={fromDate}
            max={todayIso()}
            onChange={(e) => setToDate(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="pca-page__refresh"
          onClick={() => {
            setFromDate(daysAgoIso(29));
            setToDate(todayIso());
          }}
        >
          Last 30 days
        </button>
      </div>

      {error ? <p className="pca-page__error">{error}</p> : null}

      <div className="pca-metrics">
        <MetricCard label="Active guests" value={formatNumber(summary.total)} hint="With activity in range" />
        <MetricCard
          label="Repeat guests"
          value={formatNumber(engagement.returning_guests)}
          hint={`${engagement.repeat_rate_percent ?? 0}% repeat rate`}
        />
        <MetricCard label="Avg value score" value={formatNumber(engagement.avg_value_score)} />
        <MetricCard label="Total sales" value={formatCurrency(totals.sales_value)} />
        <MetricCard label="Check-ins" value={formatNumber(totals.checkins)} />
        <MetricCard label="Orders" value={formatNumber(totals.orders)} />
        <MetricCard label="VIP / high value" value={formatNumber(summary.vip)} />
        <MetricCard label="At risk" value={formatNumber(summary.at_risk)} hint="No visit in 60+ days" />
      </div>

      <div className="pca-grid">
        <section className="pca-panel pca-panel--chart">
          <h2 className="pca-panel__title">Segments</h2>
          {segments.length ? (
            <CustomerSegmentsPieChart segments={segments} />
          ) : (
            <p className="pca-panel__empty">No guest activity in this range yet.</p>
          )}
        </section>

        <section className="pca-panel pca-panel--chart">
          <h2 className="pca-panel__title">Daily check-ins</h2>
          {daily.length ? (
            <CustomerDailyAreaChart data={daily} />
          ) : (
            <p className="pca-panel__empty">No check-ins in this range.</p>
          )}
        </section>

        <section className="pca-panel pca-panel--full">
          <h2 className="pca-panel__title">Top guests by value</h2>
          {topCustomers.length ? (
            <div className="pca-table-wrap">
              <table className="pca-table">
                <thead>
                  <tr>
                    <th>Guest</th>
                    <th>Segment</th>
                    <th>Value</th>
                    <th>Check-ins</th>
                    <th>Orders</th>
                    <th>Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {topCustomers.map((row) => (
                    <tr
                      key={row.face_user_id}
                      className="pca-table__row--clickable"
                      onClick={() =>
                        navigate(`/nyafaz-ep/partner/customers/${encodeURIComponent(row.face_user_id)}`)
                      }
                    >
                      <td>{row.display_name || "Guest"}</td>
                      <td>
                        <span className={segmentBadgeClass(row.segment)}>{segmentLabel(row.segment)}</span>
                      </td>
                      <td>{formatNumber(row.value_score)}</td>
                      <td>{formatNumber(row.checkin_count)}</td>
                      <td>{formatNumber(row.order_count)}</td>
                      <td>{formatCurrency(row.sales_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="pca-panel__empty">Guests appear after Face Identity check-ins or orders.</p>
          )}
        </section>
      </div>
    </div>
  );
}
